import { arrayMove } from '@dnd-kit/sortable';
import { assert, FlightForwardPointModel, FlightRoutes, FlightStatus, FuelUnit, logger } from '@fe/core';
import { IconType } from '@fe/flight-layer';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { applyPatches, enablePatches, Patch } from 'immer';
import { getWorker } from '../../../client/worker-container';
import Coordinate from '../../../common/coordinate';
import { ObjectClass } from '../../../common/enums';
import { DrawingObject } from '../../../components/drawing-object/types';
import { ExtractKeysExtend } from '../../../components/helpers/helpers';
import { FlightDetailsModel, FlightDetailsPinnedModel, FlightImpactDto } from '../../../flights/interop/flightDto';
import {
    FlightRoutesUpdated,
    GetFlightResponseData,
    MessageType,
    RouteAnalytics,
    SetWorkspacesRequest
} from '../../../services/actor-messages';
import { AppSettings } from '../../../services/settings-service';
import { WorkspaceLayout } from '../../../types/settings';
import { WorkspaceSettingWindow } from '../../../types/workspaces';
import { UDViewMapStyle, UdViewModel } from '../../../udView/udview';
import { WindowBounds, WorkspaceWindowPanelType } from '../../components/multi-window/types';
import { EventBus } from '../../contexts/event-bus';
import { GeojsonWarning } from '../../features/home/providers/CustomGeojsonProvider';
import { type RootState } from './index';
import { createView, updateView } from './views-slice';

enablePatches();

export interface WorkspaceStateItemFlight {
    details: FlightDetailsModel;
    routes: FlightRoutes;
    impacts: FlightImpactDto[];
}

export type WorkspaceWindowPanelState = {
    /** Panel ID, just a client-side ID for react to not confuse things */
    id: number;
    type: WorkspaceWindowPanelType;
    airportId?: string;
};

export interface WorkspaceWindowState {
    id: number;
    name: string;
    panels: WorkspaceWindowPanelState[];
    bounds: WindowBounds | null;
}

export interface WorkspaceStateItem {
    id: number;
    flights: {
        followed: number | null;
        selected: WorkspaceStateItemFlight[];
        settings: {
            showProfile: boolean;
            showTags: boolean;
            focus: boolean;
            encompass: boolean;
            routeFlown: {
                show: boolean;
                showLabels: boolean;
                showPoints: boolean;
            };
            flightPlan: {
                show: boolean;
                showPlanned: boolean;
                showAtc: boolean;
                showAi: boolean;
                showDistanceLabels: boolean;
                showPoints: boolean;
                showLabels: boolean;
                showEtops: boolean;
            };
            impacts: boolean;
            detailsTabIndex?: number;
        };
        fastForward:
            | {
                  active: false;
              }
            | {
                  active: true;
                  flightId: number;
                  acid: string;
                  icon: IconType;
                  points: FlightForwardPointModel[] | null;
              };
    };
    routeAi: RouteAnalytics | null;
    view: UdViewModel;
    viewLoading: boolean;
    viewModified: boolean;
    navDataWarning: string | null;
    geojsonWarnings: GeojsonWarning[];
    leftFlyout:
        | { type: 'airport'; airportId: string }
        | { type: 'diversion'; flightId: number }
        | { type: 'notes'; airportId: string }
        | null;
    holdingAnalyzer:
        | {
              active: false;
          }
        | {
              active: true;
              drawingObjectId: string;
          };
}

export interface WorkspaceState {
    selectedId: number;
    layout: WorkspaceLayout;
    items: WorkspaceStateItem[];
    individual: {
        tags: number[];
        history: number[];
        vector: number[];
        rangeRings: number[];
        flightPath: number[];
        routeFlown: number[];
    };
    flights: {
        pinned: FlightDetailsPinnedModel[];
        settings: {
            // controls whether to always show pinned flights - even if they are filtered out by filters.
            showPinned: boolean;
        };
    };
    panels: {
        fleetMonitor: boolean;
        airportMonitor: boolean;
        eventList: boolean;
        legend: boolean;
        routeAnalyzer: boolean;
    };
    windows: WorkspaceWindowState[];
    mapTools: {
        latLng: boolean;
        distanceBearing: boolean;
    };
    drawingObjects:
        | {
              state: 'idle' | 'draw';
          }
        | {
              state: 'edit' | 'delete' | 'properties' | 'create';
              object: DrawingObject;
          };
}

const initialState: WorkspaceState = {
    layout: WorkspaceLayout.single,
    flights: {
        pinned: [],
        settings: {
            showPinned: true
        }
    },
    items: [],
    selectedId: 0,
    individual: {
        tags: [],
        history: [],
        vector: [],
        rangeRings: [],
        flightPath: [],
        routeFlown: []
    },
    mapTools: {
        latLng: false,
        distanceBearing: false
    },
    panels: {
        fleetMonitor: false,
        airportMonitor: false,
        eventList: false,
        legend: false,
        routeAnalyzer: false
    },
    windows: [],
    drawingObjects: {
        state: 'idle'
    }
};

export const workspaceSlice = createSlice({
    name: 'workspace',
    initialState: initialState,
    reducers: {
        applyPatchWorkspaceViewView: (state, params: { payload: { patches: Patch[]; workspaceId: number } }) => {
            if (params.payload.workspaceId >= state.items.length) {
                return;
            }
            applyPatches(state.items[params.payload.workspaceId].view, params.payload.patches);
            state.items[state.selectedId].viewModified = true;
        },
        applyPatchWorkspaceItem: (state, params: PayloadAction<{ patch: Patch[]; workspaceId: number }>) => {
            applyPatches(state.items[params.payload.workspaceId], params.payload.patch);
        },
        initializeWorkspaceFromSettings: (
            state,
            params: PayloadAction<{
                settings: AppSettings;
                views: { workspaceId: number; view: UdViewModel }[];
                defaultView: UdViewModel;
                isDark: boolean;
            }>
        ) => {
            const config = params.payload.settings.workspaceConfig;
            state.layout = config.layout;
            state.items = new Array(workspaceItemCount(config.layout))
                .fill(0)
                .map((_, id) =>
                    newWorkspaceItem(
                        id,
                        params.payload.views.find((x) => x.workspaceId === id)?.view ?? params.payload.defaultView,
                        params.payload.settings
                    )
                );
            state.panels = {
                fleetMonitor: config.panels.fleetMonitor,
                airportMonitor: config.panels.airportMonitor,
                eventList: config.panels.eventList,
                legend: config.panels.legend,
                routeAnalyzer: config.panels.routeAnalyzer ?? false
            };
            state.flights.settings.showPinned = params.payload.settings.showWatchedAircraftIcons ?? true;
            state.windows = config.windows.map((x) => workspaceWindowModelToState(x)).filter((x) => x !== null);

            for (const item of state.items) {
                // Check for default views
                if (!item.view.id) {
                    // If the view has one of those styles, keep it in sync with app theme
                    if (item.view.map.style === UDViewMapStyle.light || item.view.map.style === UDViewMapStyle.dark) {
                        // And set default style
                        item.view = {
                            ...item.view,
                            map: {
                                ...item.view.map,
                                style: params.payload.isDark ? UDViewMapStyle.dark : UDViewMapStyle.light
                            }
                        };
                    }
                }
            }
        },
        setWorkspaceLayout: (
            state,
            params: PayloadAction<{
                settings: AppSettings;
                layout: WorkspaceLayout;
                views: { workspaceId: number; view: UdViewModel }[];
                defaultView: UdViewModel;
            }>
        ) => {
            state.layout = params.payload.layout;
            const newLength = workspaceItemCount(params.payload.layout);
            if (newLength === state.items.length) {
                return;
            }

            if (newLength < state.items.length) {
                state.items = state.items.slice(0, newLength);
            } else {
                for (let id = state.items.length; id < newLength; id++) {
                    state.items.push(
                        newWorkspaceItem(
                            id,
                            params.payload.views.find((x) => x.workspaceId === id)?.view ?? params.payload.defaultView,
                            params.payload.settings
                        )
                    );
                }
            }

            if (state.selectedId >= state.items.length) {
                state.selectedId = 0;
            }
        },
        setFocusedWorkspaceItem: (state, params: { payload: { id: number } }) => {
            if (state.drawingObjects.state !== 'idle') {
                return;
            }

            state.selectedId = params.payload.id;
        },
        clearAllSelectionCurrentWorkspace(state) {
            state.items[state.selectedId].flights.selected = [];
        },
        updateSelectedFlightDetails: (state, params: { payload: FlightDetailsModel[] }) => {
            const selected = state.items[state.selectedId].flights.selected;
            for (const details of params.payload) {
                const idx = selected.findIndex((x) => x.details.id === details.id);
                if (idx < 0) {
                    continue;
                }
                selected[idx].details = details;
            }
        },
        updateFlightRoutes: (state, params: { payload: FlightRoutesUpdated }) => {
            for (const item of state.items) {
                const flight = item.flights.selected.find((x) => x.details.id === params.payload.flightId);
                if (flight) {
                    // Keep the old route if null
                    // TODO: find a better way of doing this. Routes should be undefined in params.payload when they are not supposed to be updated
                    flight.routes.planned = params.payload.planned ?? flight.routes.planned;
                    flight.routes.atc = params.payload.atc ?? flight.routes.atc;
                    flight.routes.flown = params.payload.flown ?? flight.routes.flown;
                    flight.routes.destination = params.payload.destination;
                }
            }
        },
        updateFlightsRoutes: (state, params: { payload: FlightRoutesUpdated[] }) => {
            for (const item of state.items) {
                for (const update of params.payload) {
                    const flight = item.flights.selected.find((x) => x.details.id === update.flightId);
                    if (flight) {
                        // Keep the old route if null
                        flight.routes.planned = update.planned ?? flight.routes.planned;
                        flight.routes.atc = update.atc ?? flight.routes.atc;
                        flight.routes.flown = update.flown ?? flight.routes.flown;
                        flight.routes.destination = update.destination;
                    }
                }
            }
        },
        removeTutorialSelectedFlight: (state) => {
            state.items[state.selectedId].flights.selected = state.items[state.selectedId].flights.selected.filter(
                (x) => x.details.id !== -1
            );
        },
        setTutorialSelectedFlight: (state) => {
            state.items[state.selectedId].flights.selected.unshift({
                details: {
                    id: -1,
                    acid: 'FE123',
                    acType: 'A320',
                    regId: 'N123AB',
                    status: FlightStatus.Active,
                    ground: false,
                    class: ObjectClass.Air,
                    position: { isValid: true, lat: 30, lng: 30 } as Coordinate,
                    altitude: 38000,
                    speed: 430,
                    origin: 'KLAX',
                    destination: 'KJFK',
                    destinationAirportPosition: { isValid: true, lat: 30, lng: 30 } as Coordinate,
                    originAirportPosition: { isValid: true, lat: 30, lng: 30 } as Coordinate,
                    distanceToDestination: null,
                    progressPercentage: null,
                    departureTime: dayjs().add(-3.45, 'hour').unix(),
                    arrivalTime: dayjs().add(3.45, 'hour').unix(),
                    lastPositionReportTime: dayjs().add(-30, 'second').unix(),
                    lastReal: { isValid: true, lat: 30, lng: 30 } as Coordinate,
                    lastRealAltitude: null,
                    lastRealSpeed: null,
                    icaoId: 0xabc123,
                    feedId: 190,
                    userField1: '01',
                    destinationAlternate1: 'KBDL',
                    destinationAlternate2: 'KBOS',
                    out: dayjs().add(-3.55, 'hour').unix(),
                    off: dayjs().add(-3.44, 'hour').unix(),
                    on: null,
                    in: null,
                    fuelOnBoard: {
                        value: 2464,
                        unit: FuelUnit.WeighPounds
                    },
                    scratchpad: '',
                    breachedThresholdsCount: 1,
                    beacon: null
                },
                routes: {
                    atc: null,
                    planned: null,
                    flown: null,
                    destination: null
                },
                impacts: []
            });
        },
        updateSelectedFlights: (state, params: { payload: { add: GetFlightResponseData | null; remove: number[] } }) => {
            const workspace = state.items[state.selectedId];
            if (params.payload.remove.length > 0) {
                workspace.flights.selected = workspace.flights.selected.filter((x) => !params.payload.remove.includes(x.details.id));
            }

            if (params.payload.add) {
                workspace.flights.selected.unshift({
                    details: params.payload.add.details,
                    routes: params.payload.add.routes,
                    impacts: params.payload.add.impacts
                });
            }
        },
        upsertPinnedFlight: (state, params: { payload: FlightDetailsPinnedModel[] }) => {
            // FIXME: pinned flights can belong to different workspace items
            // go over all workspace and update pinned flights
            // TODO: maybe pinned flights will be defined at workspace level
            for (const flight of params.payload) {
                const idx = state.flights.pinned.findIndex((x) => x.id === flight.id);
                if (idx > -1) {
                    state.flights.pinned[idx] = flight;
                } else {
                    state.flights.pinned.push(flight);
                }
            }
        },
        changeFlightDetailsTabIndex: (state, params: { payload: number | undefined }) => {
            const workspace = state.items[state.selectedId];
            workspace.flights.settings.detailsTabIndex = params.payload;
        },
        toggleFlightIndividual: (state, params: { payload: { id: number; type: keyof WorkspaceState['individual'] } }) => {
            const workspace = state;
            const individual = workspace.individual[params.payload.type as keyof WorkspaceState['individual']];
            if (individual.includes(params.payload.id)) {
                workspace.individual[params.payload.type as keyof WorkspaceState['individual']] = individual.filter(
                    (x) => x !== params.payload.id
                );
            } else {
                individual.push(params.payload.id);
            }
        },
        updateRouteFlownSettings: (
            state,
            params: { payload: { key: keyof WorkspaceStateItem['flights']['settings']['routeFlown']; value: any } }
        ) => {
            state.items[state.selectedId].flights.settings.routeFlown[params.payload.key] = params.payload.value;
        },
        updateFlightPlanSettings: (
            state,
            params: { payload: { key: keyof WorkspaceStateItem['flights']['settings']['flightPlan']; value: any } }
        ) => {
            state.items[state.selectedId].flights.settings.flightPlan[params.payload.key] = params.payload.value;
        },
        updateWorkspaceSettings: (state, params: { payload: { key: keyof WorkspaceStateItem; value: any } }) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state.items[state.selectedId][params.payload.key] = params.payload.value;
        },
        startDrawDrawingObjectWorkspace: (state) => {
            state.drawingObjects = {
                state: 'draw'
            };
        },
        createDrawingObjectWorkspace: (state, params: { payload: { object: DrawingObject } }) => {
            state.drawingObjects = {
                state: 'create',
                object: params.payload.object
            };
        },
        editDrawingObjectWorkspace: (state, params: { payload: { object: DrawingObject } }) => {
            state.drawingObjects = {
                state: 'edit',
                object: params.payload.object
            };
        },
        propertiesDrawingObjectWorkspace: (state, params: { payload: { object: DrawingObject } }) => {
            state.drawingObjects = {
                state: 'properties',
                object: params.payload.object
            };
        },
        deleteDrawingObjectWorkspace: (state, params: { payload: { object: DrawingObject } }) => {
            state.drawingObjects = {
                state: 'delete',
                object: params.payload.object
            };
        },
        drawingObjectSetIdleWorkspace: (state) => {
            state.drawingObjects = {
                state: 'idle'
            };
        },
        enableHoldingAnalyzer: (state, params: { payload: { workspaceId: number; drawingObjectId: string } }) => {
            state.items[params.payload.workspaceId].holdingAnalyzer = {
                active: true,
                drawingObjectId: params.payload.drawingObjectId
            };
        },
        disableHoldingAnalyzer: (state, params: { payload: { workspaceId: number } }) => {
            state.items[params.payload.workspaceId].holdingAnalyzer = {
                active: false
            };
        },
        togglePinnedVisibility: (state) => {
            state.flights.settings.showPinned = !state.flights.settings.showPinned;
        },
        followFlight(state, params: { payload: number | null }) {
            const current = state.items[state.selectedId];
            current.flights.followed = params.payload;
        },
        setLatLngTool(state, params: { payload: boolean }) {
            state.mapTools.latLng = params.payload;
        },
        setDistanceBearingTool(state, params: { payload: boolean }) {
            state.mapTools.distanceBearing = params.payload;
        },
        setWorkspaceViewLoaded(state, params: PayloadAction<{ workspaceId: number }>) {
            state.items[params.payload.workspaceId].viewLoading = false;
        },
        setWorkspacePanelVisibility(
            state,
            params: PayloadAction<{ panel: ExtractKeysExtend<WorkspaceState['panels'], boolean>; visible: boolean }>
        ) {
            state.panels[params.payload.panel] = params.payload.visible;
        },
        // windows
        createWindow(state, params: PayloadAction<{ initialPanel: WorkspaceWindowPanelType; airportId?: string }>) {
            let i = 0;
            while (state.windows[i]?.id === i) {
                i++;
            }

            state.windows.push({
                name: 'Window ' + (i + 1),
                id: i,
                panels: [
                    {
                        id: 0,
                        type: params.payload.initialPanel,
                        airportId: params.payload.airportId
                    }
                ],
                bounds: null
            });

            state.windows.sort((a, b) => a.id - b.id);
        },
        closeWindow(state, params: PayloadAction<{ id: number }>) {
            const idx = state.windows.findIndex((x) => x.id === params.payload.id);
            if (idx !== -1) {
                state.windows.splice(idx, 1);
            }
        },
        closeAllWindows(state) {
            state.windows = [];
        },
        updateWindowBounds(state, params: PayloadAction<{ id: number; bounds: WindowBounds }>) {
            const window = state.windows.find((x) => x.id === params.payload.id);
            if (window) {
                window.bounds = params.payload.bounds;
            }
        },
        setWindowPanelOrder(state, params: PayloadAction<{ windowId: number; panelId: number; swapWithId: number }>) {
            const window = state.windows.find((x) => x.id === params.payload.windowId);
            if (window) {
                const oldIdx = window.panels.findIndex((x) => x.id === params.payload.panelId);
                const newIdx = window.panels.findIndex((x) => x.id === params.payload.swapWithId);
                if (oldIdx !== -1 && newIdx !== -1) {
                    window.panels = arrayMove(window.panels, oldIdx, newIdx);
                }
            }
        },
        addPanelToWindow(state, params: PayloadAction<{ windowId: number; panel: WorkspaceWindowPanelType; airportId?: string }>) {
            const window = state.windows.find((x) => x.id === params.payload.windowId);
            if (window) {
                const id = window.panels.reduce((p, n) => Math.max(p, n.id), -1) + 1;
                window.panels.push({
                    id,
                    type: params.payload.panel,
                    airportId: params.payload.airportId
                });
            }
        },
        updatePanelInWindow(
            state,
            params: PayloadAction<{
                windowId: number;
                update: Partial<Pick<WorkspaceWindowPanelState, 'airportId'>> & { id: number };
            }>
        ) {
            const window = state.windows.find((x) => x.id === params.payload.windowId);
            const panel = window?.panels.find((x) => x.id === params.payload.update.id);
            if (panel) {
                Object.assign(panel, params.payload.update);
            }
        },
        removePanelFromWindow(state, params: PayloadAction<{ windowId: number; panelId: number }>) {
            const window = state.windows.find((x) => x.id === params.payload.windowId);
            if (!window) {
                return;
            }
            window.panels = window.panels.filter((x) => x.id !== params.payload.panelId);
        },
        setNavDataWarning(state, params: PayloadAction<{ workspaceId: number; message: string | null }>) {
            const item = state.items[params.payload.workspaceId];
            if (item) {
                item.navDataWarning = params.payload.message;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(updateView.fulfilled, (state, action) => {
            // Check if it's one of the views currently selected
            const item = Object.values(state.items).find((x) => x.view.id === action.meta.arg.view.id);
            if (!item) {
                return;
            }
            item.view = action.payload;
            item.viewModified = false;
        });

        builder.addCase(createView.fulfilled, (state, action) => {
            // When a view is created it is created as a copy of currently selected view
            const item = state.items[action.meta.arg.workspaceId];
            // All properties apart from the following are the same between the newly created view and the view it is based on
            item.view.id = action.payload.id;
            item.view.isFavorite = action.payload.isFavorite;
            item.view.type = action.payload.type;
            item.view.name = action.payload.name;
            item.view.includeInSlideshow = action.payload.includeInSlideshow;

            // Since this is a new view it's not modified yet
            item.viewModified = false;
        });

        builder.addCase(workspaceSwitchView.fulfilled, (state, action) => {
            const item = state.items[action.payload.workspaceId];
            item.view = action.payload.view;
            item.navDataWarning = null;
            item.viewLoading = false;
            item.viewModified = false;
        });

        builder.addCase(workspaceSwitchView.pending, (state, action) => {
            const item = state.items[action.meta.arg.workspaceId];
            item.viewLoading = true;
        });

        builder.addCase(addPinnedFlight.fulfilled, (state, action) => {
            if (!action.payload) {
                return;
            }
            state.flights.pinned.push(action.payload);
        });

        builder.addCase(removePinnedFlight.fulfilled, (state, action) => {
            const flights = state.flights.pinned;
            const idx = flights.findIndex((x) => x.id === action.meta.arg.flightId);
            if (idx !== -1) {
                flights.splice(idx, 1);
            }
        });

        builder.addCase(removeAllPinnedFlights.fulfilled, (state) => {
            state.flights.pinned = [];
        });

        builder.addCase(fastForwardFlight.fulfilled, (state, action) => {
            const workspaceItem = state.items[action.meta.arg.workspaceId];
            if (!action.payload) {
                workspaceItem.flights.fastForward = {
                    active: false
                };
                return;
            }

            workspaceItem.flights.fastForward = {
                active: true,
                acid: action.payload.acid,
                icon: action.payload.icon,
                flightId: action.meta.arg.flightId,
                points: action.payload.points
            };
        });
    }
});

export function toggleLeftFlyout(flyout?: WorkspaceStateItem['leftFlyout']) {
    return workspaceSlice.actions.updateWorkspaceSettings({ key: 'leftFlyout', value: flyout || null });
}

export function updateRouteFlownSettings<K extends keyof WorkspaceStateItem['flights']['settings']['routeFlown']>(
    key: K,
    value: WorkspaceStateItem['flights']['settings']['routeFlown'][K]
) {
    return workspaceSlice.actions.updateRouteFlownSettings({ key, value });
}

export function updateFlightPlanSettings<K extends keyof WorkspaceStateItem['flights']['settings']['flightPlan']>(
    key: K,
    value: WorkspaceStateItem['flights']['settings']['flightPlan'][K]
) {
    return workspaceSlice.actions.updateFlightPlanSettings({ key, value });
}

export const setWorkspaceLayout = createAsyncThunk('workspace/set-layout', async (layout: WorkspaceLayout, api) => {
    const state = api.getState() as RootState;
    const defaultView = state.views.items.find((x) => !x.id);
    assert(defaultView);
    const views = state.views.items;
    const settings = state.settings;
    // Grab view list stored in the workspace settings
    const viewBindings = state.settings.workspaceConfig.views
        .map((x) => ({ ...x, view: views.find((v) => v.id === x.viewId) }))
        .filter((x) => x.view)
        .map((x) => ({ workspaceId: x.workspaceId, view: x.view! }));
    // Dispatch layout change
    api.dispatch(workspaceSlice.actions.setWorkspaceLayout({ layout, views: viewBindings, defaultView, settings }));
    // Grab modified workspace items
    const workspaceItems = (api.getState() as RootState).workspace.items;
    // And get all the views
    const workspaceViews = workspaceItems.map((x) => ({ workspaceId: x.id, view: x.view }) satisfies SetWorkspacesRequest[0]);
    // TODO: consider moving this to a middleware. Would be a bit cleaner
    await getWorker().actor.sendAsync({ type: MessageType.setWorkspaces, data: workspaceViews });
});

export const workspaceSwitchView = createAsyncThunk('workspace/load-view', async (params: { workspaceId: number; view: UdViewModel }) => {
    logger.info('Workspace view changed, workspace: %d', params.workspaceId);

    await new Promise((resolve) => {
        EventBus.fire('workspace.view-selected', { resolveViewLoaded: resolve, view: params.view, workspaceId: params.workspaceId });
    });
    await getWorker().actor.sendAsync({ type: MessageType.setWorkspaceView, data: { workspaceId: params.workspaceId, view: params.view } });
    return params;
});

export const addPinnedFlight = createAsyncThunk(
    'workspace/add-pinned-flight',
    async (params: { workspaceId: number; flightId: number }) => {
        const flight = await getWorker().actor.sendAsync({ type: MessageType.addPinnedFlight, data: params.flightId });
        return flight;
    }
);

export const removePinnedFlight = createAsyncThunk(
    'workspace/remove-pinned-flight',
    async (params: { workspaceId: number; flightId: number }) => {
        await getWorker().actor.sendAsync({
            type: MessageType.removePinnedFlight,
            data: { type: 'single', id: params.flightId }
        });
    }
);

// TODO: decide is this is workspace global or workspace item scoped
export const fastForwardFlight = createAsyncThunk(
    'workspace/fast-forward-flight',
    async (params: { flightId: number; workspaceId: number }, api) => {
        const forward = await getWorker().actor.sendAsync({ type: MessageType.getFlightForward, data: params.flightId });
        return forward;
    }
);

export const removeAllPinnedFlights = createAsyncThunk('workspace/remove-all-pinned-flights', async () => {
    await getWorker().actor.sendAsync({ type: MessageType.removePinnedFlight, data: { type: 'all' } });
});

export const {
    initializeWorkspaceFromSettings,
    setWorkspaceViewLoaded,
    setFocusedWorkspaceItem,
    updateFlightRoutes,
    updateFlightsRoutes,
    updateSelectedFlightDetails,
    setTutorialSelectedFlight,
    removeTutorialSelectedFlight,
    upsertPinnedFlight,
    clearAllSelectionCurrentWorkspace,
    changeFlightDetailsTabIndex,
    toggleFlightIndividual,
    updateSelectedFlights,
    editDrawingObjectWorkspace,
    propertiesDrawingObjectWorkspace,
    deleteDrawingObjectWorkspace,
    createDrawingObjectWorkspace,
    startDrawDrawingObjectWorkspace,
    drawingObjectSetIdleWorkspace,
    enableHoldingAnalyzer,
    disableHoldingAnalyzer,
    togglePinnedVisibility,
    setLatLngTool,
    setDistanceBearingTool,
    followFlight,
    createWindow,
    closeWindow,
    closeAllWindows,
    setWorkspacePanelVisibility,
    updatePanelInWindow,
    addPanelToWindow,
    removePanelFromWindow,
    setWindowPanelOrder,
    updateWindowBounds,
    setNavDataWarning
} = workspaceSlice.actions;

function workspaceItemCount(layout: WorkspaceLayout): number {
    switch (layout) {
        case WorkspaceLayout.single:
            return 1;
        case WorkspaceLayout.dualVertical:
        case WorkspaceLayout.dualHorizontal:
            return 2;
        case WorkspaceLayout.triVertical:
        case WorkspaceLayout.triHorizontal:
            return 3;
        case WorkspaceLayout.quad:
            return 4;
    }
}

function newWorkspaceItem(id: number, view: UdViewModel, settings: AppSettings): WorkspaceStateItem {
    return {
        id,
        flights: {
            followed: null,
            selected: [],
            settings: {
                showTags: settings.showHighlightedTag,
                focus: settings.focusEnabled,
                encompass: settings.encompassEnabled,
                showProfile: settings.profileEnabled,
                impacts: true,
                routeFlown: {
                    show: settings.drawRouteFlown,
                    showLabels: settings.routeFlownLabels,
                    showPoints: settings.routeFlownPoints
                },
                flightPlan: {
                    show: settings.enablePlannedRoutes,
                    showPlanned: settings.drawPlannedRoute,
                    showAtc: settings.drawAtcRoute,
                    showAi: false,
                    showDistanceLabels: settings.plannedRouteTimeDistance,
                    showPoints: settings.plannedRoutePoints,
                    showLabels: settings.plannedRouteLabels,
                    showEtops: settings.showEtops
                }
            },
            fastForward: {
                active: false
            }
        },
        routeAi: null,
        view,
        viewLoading: false,
        viewModified: false,
        navDataWarning: null,
        geojsonWarnings: [],
        leftFlyout: null,
        holdingAnalyzer: {
            active: false
        }
    };
}

function workspaceWindowModelToState(window: WorkspaceSettingWindow): WorkspaceWindowState | null {
    if (window.version === undefined) {
        return {
            id: window.id,
            name: `Window ${window.id + 1}`,
            panels: (window.panelOrder?.filter((t) => window.panels.includes(t)) ?? window.panels).map((x, i) => ({
                id: i,
                type: x
            })),
            bounds: window.bounds
                ? {
                      screenX: window.bounds.x,
                      screenY: window.bounds.y,
                      width: window.bounds.width,
                      height: window.bounds.height
                  }
                : null
        };
    } else if (window.version === 2) {
        return {
            id: window.id,
            name: `Window ${window.id + 1}`,
            panels: window.panels.map((x, i) => ({
                id: i,
                type: x.type,
                airportId: x.airportId
            })),
            bounds: window.bounds
                ? {
                      screenX: window.bounds.x,
                      screenY: window.bounds.y,
                      width: window.bounds.width,
                      height: window.bounds.height
                  }
                : null
        };
    }

    logger.warn('Unrecognized window version', window);
    return null;
}
